import { useForm, FormProvider } from "react-hook-form";
import { RegisterOptions } from "react-hook-form/dist/types/validator";

import { InputCombo } from "shared/forms/InputCombo";
import { Button } from "ui/Button";
import { StepperActions } from "utils/useStepper";
import { isEmail } from "utils/validators.utils";
import { useI18n } from "utils/with-i18n.utils";

import { useCreateProductReminderMutation } from "./create-product-reminder.mutation.generated";

type NotifyContentFormData = { email: string };

type NotifyContentProps = {
  isComingSoon?: boolean;
  productName?: string | null;
  subscriptionId: number;
  goToNextStep: StepperActions["goToNextStep"];
};

export const NotifyContent = ({
  isComingSoon,
  productName,
  subscriptionId,
  goToNextStep,
}: NotifyContentProps) => {
  const i18n = useI18n();
  const form = useForm<NotifyContentFormData>();
  const [createOutOfStockReminder] = useCreateProductReminderMutation();

  const onSubmit = form.handleSubmit(async ({ email }) => {
    await createOutOfStockReminder({
      variables: {
        customerReminder: {
          email,
          subscriptionId,
        },
      },
    });
    goToNextStep();
  });

  const emailFieldRules: RegisterOptions = {
    required: {
      value: true,
      message: i18n.t("product.reminder.form.email.required"),
    },
    validate: (email: string) => {
      return isEmail(email) || i18n.t("product.outOfStock.form.email.pattern");
    },
  };

  const isIphone = productName?.toLowerCase().includes("iphone");

  return (
    <FormProvider {...form}>
      <form
        className="flex flex-col gap-4 justify-center items-center text-center"
        onSubmit={onSubmit}
      >
        <p className="text-3xl font-semibold">
          {isComingSoon ? (
            i18n.t(
              isIphone
                ? "product.comingSoon.moda.title.iphone"
                : "product.comingSoon.moda.title.samsung",
              {
                product: productName,
              },
            )
          ) : (
            <>
              {i18n.t("product.outOfStock.backVictim")}
              <br className="hidden lg:block" />
              <span className="text-cornflower-blue">
                {i18n.t("product.outOfStock.backSoon")}
              </span>
            </>
          )}
        </p>
        <p className="text-gray">
          {i18n.t(
            isComingSoon
              ? "product.comingSoon.leaveEmail"
              : "product.outOfStock.leaveEmail",
          )}
        </p>
        <InputCombo
          fieldName="email"
          autoCapitalize="none"
          fieldOptions={emailFieldRules}
          placeholder={i18n.t("product.reminder.form.email")}
          className="w-full lg:w-[70%]"
          {...form.register("email")}
        />
        <Button type="submit" isLoading={form.formState.isSubmitting}>
          {i18n.t("product.outOfStock.warnMe")}
        </Button>
      </form>
    </FormProvider>
  );
};
